import React, { useContext } from 'react';
import { UserOutlined, SyncOutlined, StopOutlined, UserDeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { Flex, Popconfirm, Typography, type MenuProps } from 'antd';
import { SubscriptionStatusType } from '../../../helpers';
import { ISubscriptionItem, ModalType } from '../controllers/useSubscriptionsPageController';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import { UserRole } from '@leedpulse-monorepo/enums';

interface ActionMenuItemsParams {
  record: ISubscriptionItem;
  onOpenModal: (type: ModalType, subscription: ISubscriptionItem) => void;
  onUnassign: (subscription: ISubscriptionItem) => void;
  onCancelPlanChange: (subscription: ISubscriptionItem) => void;
  /** Resolved plans for subscription rows (id_plan → Plan); used to hide change-plan when no allowed targets */
  planMap?: Map<string, IPlan>;
  isDegradedMode: boolean;
  isCompanyExternallyBilled: boolean;
}

export const getActionMenuItems = ({ record, onOpenModal, onUnassign, onCancelPlanChange, planMap, isDegradedMode, isCompanyExternallyBilled }: ActionMenuItemsParams) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks 
  const authentication = useContext(AuthenticationContext); // this func is always called in a react component so we can usecontext here

  // Show "Réassigner" only if the number actually has a user assigned
  const hasAssignedUser = record.usernames && record.usernames.length > 0;
  const isPendingCancellation = record.status === SubscriptionStatusType.RESILIATION_EN_COURS;

  const isAssignBlocked = record.status === SubscriptionStatusType.RESILIÉ || record.status === SubscriptionStatusType.PAUSED;

  // Prevent menu clicks from bubbling to the table row (opens consumption history).
  const openModalFromMenu = (type: ModalType) => ({ domEvent }: { domEvent: React.SyntheticEvent }) => {
    domEvent.stopPropagation();
    onOpenModal(type, record);
  };

  const items: NonNullable<MenuProps['items']> = [
    {
      key: 'assign',
      label: hasAssignedUser ? 'Réassigner' : 'Assigner à un utilisateur',
      icon: <UserOutlined />,
      onClick: openModalFromMenu('ASSIGN_NUMBER'),
      disabled: isAssignBlocked,
    },
  ];

  if (hasAssignedUser) {
    items.push({
      key: 'unassign',
      icon: <UserDeleteOutlined />,
      // Keep modal unassign path; this is only a shortcut.
      label: (
        <Popconfirm
          title="Désassigner ce numéro ?"
          description="Le numéro sera immédiatement libéré."
          okText="Désassigner"
          cancelText="Annuler"
          onConfirm={() => onUnassign(record)}
          disabled={isDegradedMode && !isCompanyExternallyBilled}
        >
          <span onClick={(e) => e.stopPropagation()}>Désassigner</span>
        </Popconfirm>
      ),
      onClick: ({ domEvent }) => domEvent.stopPropagation(),
      disabled: isDegradedMode && !isCompanyExternallyBilled,
    });
  }

  // Only show "Changer d'abonnement" when the current plan allows at least one target (upgradableTo / downgradableTo)
  const planForRow = record.id_plan ? planMap?.get(record.id_plan) : undefined;
  const canChangeSubscription =
    !!planForRow &&
    ((planForRow.upgradable_to ?? []).length > 0 || (planForRow.downgradable_to ?? []).length > 0);

  // "Résilier" (and optionally change plan) only if the number is NOT pending cancellation
  if (!isPendingCancellation) {
    if (canChangeSubscription) {
      items.push({
        key: 'change-subscription',
        label: "Changer d'abonnement",
        icon: <SyncOutlined />,
        onClick: openModalFromMenu('CHANGE_SUBSCRIPTION'),
        disabled: isDegradedMode || isCompanyExternallyBilled,
      });
    }

    // A downsell stays cancellable until it applies, on the next billing date
    if (record.pending_plan_change) {
      items.push({
        key: 'cancel-plan-change',
        icon: <UndoOutlined />,
        label: (
          <Popconfirm
            title="Annuler le changement de plan ?"
            description={`Le passage au plan inférieur prévu le ${new Date(record.pending_plan_change.effective_at).toLocaleDateString('fr-FR')} sera annulé.`}
            okText="Annuler le changement"
            cancelText="Fermer"
            onConfirm={() => onCancelPlanChange(record)}
            disabled={isDegradedMode || isCompanyExternallyBilled}
          >
            <span onClick={(e) => e.stopPropagation()}>Annuler le changement prévu</span>
          </Popconfirm>
        ),
        onClick: ({ domEvent }) => domEvent.stopPropagation(),
        disabled: isDegradedMode || isCompanyExternallyBilled,
      });
    }
    items.push({
      key: 'cancel',
      label: 'Résilier le numéro',
      icon: <StopOutlined />,
      danger: true,
      onClick: openModalFromMenu('CANCEL_NUMBER'),
      disabled: isDegradedMode || isCompanyExternallyBilled,
    });
  }

  if ([UserRole.owner].includes(authentication.authentication?.role ?? UserRole.customer)) {
    items.push({
      key: 'dev-infos',
      type: 'divider',
    });
    items.push({
      key: 'dev-infos',
      type: 'group',
      label: <Flex vertical>
        <Typography.Text style={{ color: 'var(--text-secondary-color)' }}>id: {record.id}</Typography.Text>
        <Typography.Text style={{ color: 'var(--text-secondary-color)' }}>status: {record.status}</Typography.Text>
        <Typography.Text style={{ color: 'var(--text-secondary-color)' }}>leedbucks: {record.credits.leedbucks ?? 0}</Typography.Text>
      </Flex>,
    });
  }

  return items;
};
